/**
 * Meta WhatsApp template languages.
 *
 * `value` is the language code sent to Meta.
 * Keep this centralized so template creation, filters and table rendering
 * always use the same labels.
 */
export const templatesLanguages = [
	{ value: 'en', label: 'English' },
	{ value: 'en_US', label: 'English (US)' },
	{ value: 'en_GB', label: 'English (UK)' },

	{ value: 'ar', label: 'Arabic' },
	{ value: 'ar_AE', label: 'Arabic (UAE)' },
	{ value: 'ar_SA', label: 'Arabic (Saudi Arabia)' },
	{ value: 'ar_EG', label: 'Arabic (Egypt)' },

	{ value: 'fr', label: 'French' },
	{ value: 'fr_FR', label: 'French (France)' },

	{ value: 'es', label: 'Spanish' },
	{ value: 'es_ES', label: 'Spanish (Spain)' },

	{ value: 'de', label: 'German' },
	{ value: 'it', label: 'Italian' },
	{ value: 'pt_BR', label: 'Portuguese (Brazil)' },
	{ value: 'pt_PT', label: 'Portuguese (Portugal)' },

	{ value: 'hi', label: 'Hindi' },
	{ value: 'ur', label: 'Urdu' },

	{ value: 'ru', label: 'Russian' },
	{ value: 'tr', label: 'Turkish' },

	{ value: 'zh_CN', label: 'Chinese (Simplified)' },
	{ value: 'zh_TW', label: 'Chinese (Traditional)' },

	{ value: 'ja', label: 'Japanese' },
	{ value: 'ko', label: 'Korean' },

	{ value: 'id', label: 'Indonesian' },
	{ value: 'ms', label: 'Malay' },
];

/**
 * Return a readable label while safely falling back to the Meta code.
 */
export const getTemplateLanguageLabel = (languageCode) => {
	if (!languageCode) return '—';

	return (
		templatesLanguages.find(
			(language) => language.value === languageCode
		)?.label ?? languageCode
	);
};

/**
 * Template statuses used only for UI filtering/display.
 *
 * Do not use this list as backend validation because Meta can introduce
 * additional statuses.
 */
export const templateStatuses = [
	{ value: 'APPROVED', label: 'Approved' },
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'REJECTED', label: 'Rejected' },
	{ value: 'PAUSED', label: 'Paused' },
	{ value: 'DISABLED', label: 'Disabled' },
];

/**
 * Creation categories supported by the standard text-template editor.
 *
 * Authentication should have its own dedicated creator because its payload
 * and UX differ from ordinary Marketing/Utility templates.
 */
export const templateCategories = [
	{
		value: 'MARKETING',
		label: 'Marketing',
		description:
			'Promotions, offers, product updates and customer engagement.',
	},
	{
		value: 'UTILITY',
		label: 'Utility',
		description:
			'Transactional and service-related customer updates.',
	},
];

export const getTemplateStatusColorScheme = (status) => {
	switch (String(status ?? '').toUpperCase()) {
		case 'APPROVED':
			return 'green';

		case 'PENDING':
			return 'yellow';

		case 'REJECTED':
			return 'red';

		case 'PAUSED':
			return 'orange';

		case 'DISABLED':
			return 'gray';

		default:
			return 'gray';
	}
};

export const getTemplateQualityColorScheme = (quality) => {
	switch (String(quality ?? '').toUpperCase()) {
		case 'GREEN':
			return 'green';

		case 'YELLOW':
			return 'yellow';

		case 'RED':
			return 'red';

		default:
			return 'gray';
	}
};